"use client";

import { useEffect, useSyncExternalStore } from "react";
import { API_BASE } from "./api";
import { track } from "./analytics";

export interface AuthUser {
  bnet_id: string;
  battletag: string;
  region: string;
  /** Backend-derived: true iff this user's battletag matches the
   *  server's ADMIN_BATTLETAG env var. Sole gate for admin features. */
  is_admin: boolean;
}

export type AuthStatus =
  | { state: "loading"; user: null }
  | { state: "anonymous"; user: null }
  | { state: "signed-in"; user: AuthUser };

// Module-level constants so getSnapshot / getServerSnapshot return a
// STABLE reference between calls. Returning a fresh `{ state, user }`
// literal each call triggers React 19's "result of getServerSnapshot
// should be cached to avoid an infinite loop" guard, which tears the
// component tree down and remounts forever.
const LOADING: AuthStatus = { state: "loading", user: null };
const ANONYMOUS: AuthStatus = { state: "anonymous", user: null };

let current: AuthStatus = LOADING;
let checkStarted = false;
const listeners = new Set<() => void>();

function emit() { for (const l of listeners) l(); }
function subscribe(cb: () => void) { listeners.add(cb); return () => listeners.delete(cb); }
function snapshot(): AuthStatus { return current; }
function serverSnapshot(): AuthStatus { return LOADING; }

async function checkAuth() {
  if (checkStarted) return;
  checkStarted = true;
  try {
    const resp = await fetch(`${API_BASE}/api/auth/me`, {
      credentials: "include",
    });
    if (resp.ok) {
      const user = (await resp.json()) as AuthUser;
      current = { state: "signed-in", user };
      // Fires on every page load for a signed-in user, not just on
      // fresh sign-in. Use `is_admin` as a coarse signal in the
      // funnel; for "completed first-time sign-in" rely on PostHog
      // session ordering.
      track("signed_in_session_resolved", {
        region: user.region,
        is_admin: user.is_admin,
      });
      // Notify listeners of state.ts so it can pull /api/state.
      onSignInCallbacks.forEach((cb) => {
        try { cb(user); } catch { /* keep going */ }
      });
    } else {
      current = ANONYMOUS;
    }
  } catch {
    current = ANONYMOUS;
  }
  emit();
}

const onSignInCallbacks = new Set<(u: AuthUser) => void>();

/** state.ts subscribes here to pull the server-side CharState exactly once
 *  per session, right after we discover the user is signed in. */
export function onSignIn(cb: (u: AuthUser) => void): () => void {
  onSignInCallbacks.add(cb);
  // If already signed in, fire immediately.
  if (current.state === "signed-in") {
    try { cb(current.user); } catch { /* ignore */ }
  }
  return () => onSignInCallbacks.delete(cb);
}

/** Hook: returns the current auth status. */
export function useAuth(): AuthStatus {
  const status = useSyncExternalStore(subscribe, snapshot, serverSnapshot);
  useEffect(() => { void checkAuth(); }, []);
  return status;
}

/** Build the full URL to the backend's Battle.net login redirect. */
export function bnetLoginUrl(region: string = "eu"): string {
  track("signin_started", { region });
  return `${API_BASE}/api/auth/bnet/login?region=${encodeURIComponent(region)}`;
}

/** Sign out: clears the session cookie and reloads to landing. */
export async function logout(): Promise<void> {
  try {
    await fetch(`${API_BASE}/api/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
  } catch { /* even on network error, force the UI back to anon */ }
  current = ANONYMOUS;
  emit();
  if (typeof window !== "undefined") {
    window.location.href = "/";
  }
}
